import type { DbTemplateStatus } from '@domain/schemas/contract.schema';
import type { GetCsvUrlResponse } from '@responses/csv';
import type { GetTemplateResponse, GetTemplatesResponse } from '@responses/templates';
import { queryOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { getRequestAuthPayload } from '../helpers/getRequestAuth';

import type { QueryFactory } from './factory.types';

/** Template row as returned by `GET /templates`, with `status` narrowed to the statuses a template can have. */
export type TemplateShort = Omit<GetTemplatesResponse[number], 'status'> & { status: DbTemplateStatus };

export const fetchTemplate = async (id: string): Promise<GetTemplateResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/templates/${id}`,
  });

  const response = await axios(networkPayload);
  return response.data;
};

export const fetchTemplates = async (): Promise<TemplateShort[]> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/templates',
  });

  const response = await axios(networkPayload);
  return response.data;
};

export const fetchTemplatesCSVDownloadUrl = async (): Promise<string> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/templates',
    params: { format: 'csv' },
  });

  const response = await axios<GetCsvUrlResponse>(networkPayload);
  return response.data.downloadUrl;
};

export const templateQueries = {
  list: () =>
    queryOptions({
      queryKey: ['templates', 'list'] as const,
      queryFn: fetchTemplates,
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: ['templates', 'detail', id] as const,
      queryFn: () => fetchTemplate(id),
    }),
} satisfies QueryFactory;

export function useTemplates() {
  return useQuery(templateQueries.list());
}
